import {
  createChartFrame,
  resolveTitle,
  resolvePalette,
  clearSvg,
  getLayout,
  appendGrid,
  appendAxes,
  addLabel,
  renderLegend,
  createSvgEl,
  scaleLinear,
  toNumber,
  normalizeSeriesColors
} from './chart-utils.js';

const DEFAULT_SERIES = [
  { name: 'Drift', values: [2, 4, 3, 6, 4, 7, 5] },
  { name: 'Lift', values: [1, 3, 2, 4, 3, 5, 4] }
];

export function render({ node }) {
  const { root, svg, legend, titleEl } = createChartFrame(node, 'Area Chart');

  const update = (next) => {
    titleEl.textContent = resolveTitle(next, 'Area Chart');
    const palette = resolvePalette(next);
    let series = normalizeSeriesColors(resolveSeries(next), palette);

    clearSvg(svg);
    const layout = getLayout();
    appendGrid(svg, layout);
    appendAxes(svg, layout);

    const pointsBySeries = series.map((entry) => seriesToPoints(entry));
    const bounds = computeBounds(pointsBySeries, next);
    const baseline = Number.isFinite(next?.props?.baseline)
      ? Number(next.props.baseline)
      : Math.min(0, bounds.yMin);

    pointsBySeries.forEach((points, index) => {
      if (!points.length) return;
      const color = series[index].color;
      const areaPath = pointsToArea(points, layout, bounds, baseline);
      const area = createSvgEl('path', {
        d: areaPath,
        fill: color,
        'fill-opacity': 0.18,
        stroke: 'none'
      });
      area.classList.add('ew-chart-area');
      svg.appendChild(area);

      const linePath = pointsToPath(points, layout, bounds);
      const line = createSvgEl('path', {
        d: linePath,
        fill: 'none',
        stroke: color,
        'stroke-width': 2
      });
      line.classList.add('ew-chart-line');
      svg.appendChild(line);
    });

    addAxisLabels(svg, layout, bounds, next);
    renderLegend(legend, series, { show: next?.props?.legend !== false });
  };

  update(node);
  return { el: root, update };
}

function resolveSeries(node) {
  const raw = node?.data?.series ?? node?.props?.series;
  const values = node?.data?.values ?? node?.props?.values;
  const points = node?.data?.points ?? node?.props?.points;

  if (Array.isArray(raw)) {
    if (raw.length && typeof raw[0] === 'number') {
      return [{ name: node?.props?.label || 'Series', values: raw }];
    }
    if (raw.length && raw[0] && Array.isArray(raw[0])) {
      return raw.map((entry, i) => ({ name: `Series ${i + 1}`, values: entry }));
    }
    if (raw.length && raw[0] && typeof raw[0] === 'object') {
      return raw.map((entry, i) => ({
        name: entry.name || `Series ${i + 1}`,
        color: entry.color,
        values: Array.isArray(entry.values) ? entry.values : null,
        points: Array.isArray(entry.points) ? entry.points : null
      }));
    }
  }

  if (Array.isArray(values)) {
    return [{ name: node?.props?.label || 'Series', values }];
  }
  if (Array.isArray(points)) {
    return [{ name: node?.props?.label || 'Series', points }];
  }

  return DEFAULT_SERIES;
}

function seriesToPoints(series) {
  if (Array.isArray(series.points)) {
    return series.points.map((pt, idx) => ({
      x: toNumber(pt.x ?? idx, idx),
      y: toNumber(pt.y, 0)
    }));
  }
  if (Array.isArray(series.values)) {
    return series.values.map((val, idx) => ({ x: idx, y: toNumber(val, 0) }));
  }
  return [];
}

function computeBounds(seriesPoints, node) {
  let xMin = Infinity;
  let xMax = -Infinity;
  let yMin = Infinity;
  let yMax = -Infinity;

  seriesPoints.forEach((points) => {
    points.forEach((pt) => {
      if (pt.x < xMin) xMin = pt.x;
      if (pt.x > xMax) xMax = pt.x;
      if (pt.y < yMin) yMin = pt.y;
      if (pt.y > yMax) yMax = pt.y;
    });
  });

  if (!Number.isFinite(xMin)) xMin = 0;
  if (!Number.isFinite(xMax)) xMax = xMin + 1;
  if (!Number.isFinite(yMin)) yMin = 0;
  if (!Number.isFinite(yMax)) yMax = yMin + 1;

  const minXOverride = toNumber(node?.props?.minX ?? node?.data?.minX, NaN);
  const maxXOverride = toNumber(node?.props?.maxX ?? node?.data?.maxX, NaN);
  const minYOverride = toNumber(node?.props?.minY ?? node?.data?.minY, NaN);
  const maxYOverride = toNumber(node?.props?.maxY ?? node?.data?.maxY, NaN);

  return {
    xMin: Number.isFinite(minXOverride) ? minXOverride : xMin,
    xMax: Number.isFinite(maxXOverride) ? maxXOverride : xMax,
    yMin: Number.isFinite(minYOverride) ? minYOverride : yMin,
    yMax: Number.isFinite(maxYOverride) ? maxYOverride : yMax
  };
}

function pointsToPath(points, layout, bounds) {
  return points
    .map((pt, idx) => {
      const x = scaleLinear(pt.x, bounds.xMin, bounds.xMax, layout.inner.x, layout.inner.x + layout.inner.w);
      const y = scaleLinear(pt.y, bounds.yMin, bounds.yMax, layout.inner.y + layout.inner.h, layout.inner.y);
      return `${idx === 0 ? 'M' : 'L'} ${x} ${y}`;
    })
    .join(' ');
}

function pointsToArea(points, layout, bounds, baseline) {
  const baseY = scaleLinear(baseline, bounds.yMin, bounds.yMax, layout.inner.y + layout.inner.h, layout.inner.y);
  const startX = scaleLinear(points[0].x, bounds.xMin, bounds.xMax, layout.inner.x, layout.inner.x + layout.inner.w);
  const endX = scaleLinear(points[points.length - 1].x, bounds.xMin, bounds.xMax, layout.inner.x, layout.inner.x + layout.inner.w);
  const path = pointsToPath(points, layout, bounds);
  return `${path} L ${endX} ${baseY} L ${startX} ${baseY} Z`;
}

function addAxisLabels(svg, layout, bounds, node) {
  const labels = node?.props?.labels || node?.data?.labels;
  const yMin = formatValue(bounds.yMin);
  const yMax = formatValue(bounds.yMax);
  addLabel(svg, {
    x: layout.inner.x,
    y: layout.inner.y - 6,
    text: yMax,
    anchor: 'start'
  });
  addLabel(svg, {
    x: layout.inner.x,
    y: layout.inner.y + layout.inner.h + 14,
    text: yMin,
    anchor: 'start'
  });

  if (Array.isArray(labels) && labels.length) {
    addLabel(svg, {
      x: layout.inner.x,
      y: layout.inner.y + layout.inner.h + 14,
      text: labels[0],
      anchor: 'start',
      className: 'ew-chart-label ew-chart-label-x'
    });
    addLabel(svg, {
      x: layout.inner.x + layout.inner.w,
      y: layout.inner.y + layout.inner.h + 14,
      text: labels[labels.length - 1],
      anchor: 'end',
      className: 'ew-chart-label ew-chart-label-x'
    });
  }
}

function formatValue(value) {
  if (!Number.isFinite(value)) return '0';
  if (Math.abs(value) >= 1000) return value.toFixed(0);
  if (Math.abs(value) >= 100) return value.toFixed(1);
  return value.toFixed(2).replace(/\.00$/, '');
}
